'use client';

import { ARIA_LABELS } from '@/constants';
import { TScoreHistoryEntry } from '@/domains/scoring/score-history-policy';
import { useMemo, useState } from 'react';

/**
 * Configuration for `useScoreHistoryCard`.
 *
 * This mirrors the subset of card props required to drive list behavior,
 * sorting, and expand/collapse interactions.
 */
export interface UseScoreHistoryCardOptions {
	/** Whether the list should start expanded with all entries visible. */
	allVisible?: boolean;
	/** Raw score history entries (unsorted input from the parent component). */
	entries: TScoreHistoryEntry[];
	/** Number of entries shown when the list is collapsed. */
	initialVisibleCount?: number;
	/** Optional callback fired any time the show more/less toggle is clicked. */
	onShowMore?: () => void;
	/** Global switch controlling whether the toggle button can render. */
	showMoreButton?: boolean;
}

/**
 * Return shape for `useScoreHistoryCard`.
 *
 * The hook exposes both derived read-only state and event handlers used by
 * `ScoreHistoryCard` for rendering and interactions.
 */
export interface UseScoreHistoryCardResult {
	/** Screen reader label used by the expand/collapse control. */
	ariaExpandedLabel: string;
	/** Click handler for toggling expanded/collapsed state. */
	handleShowMore: () => void;
	/** True when there are entries beyond the collapsed visible count. */
	hasMoreEntries: boolean;
	/** Current expanded state for the score history list. */
	isExpanded: boolean;
	/** Whether the UI should render the show more/less control. */
	shouldShowMoreButton: boolean;
	/** Active collapsed entry limit; reset when collapsing. */
	visibleCount: number;
	/** Entries currently visible in the UI based on expansion state. */
	visibleEntries: TScoreHistoryEntry[];
}

/**
 * Encapsulates ScoreHistoryCard list visibility and sorting behavior.
 */
export const useScoreHistoryCard = ({
	allVisible = false,
	entries,
	initialVisibleCount = 3,
	onShowMore,
	showMoreButton = true,
}: UseScoreHistoryCardOptions): UseScoreHistoryCardResult => {
	// -------------------------------------------------------------
	// Local state
	// -------------------------------------------------------------
	// Controls how many records are visible while the list is collapsed.
	const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
	// Tracks whether the user is currently viewing the full list.
	const [isExpanded, setIsExpanded] = useState(allVisible);

	// -------------------------------------------------------------
	// Derived data
	// -------------------------------------------------------------

	// Determines whether hidden items still exist in collapsed mode.
	const hasMoreEntries = useMemo(
		() => entries.length > visibleCount,
		[entries, visibleCount],
	);

	// Toggle button should be shown only when globally enabled and there is
	// additional content available OR we are currently expanded (to allow
	// collapsing back).
	const shouldShowMoreButton = useMemo(
		() => showMoreButton && (hasMoreEntries || isExpanded),
		[showMoreButton, hasMoreEntries, isExpanded],
	);

	// Select the entries to render:
	// - expanded: show every sorted entry
	// - collapsed: show first `visibleCount` entries
	const visibleEntries = useMemo(
		() => (isExpanded ? entries : entries.slice(0, visibleCount)),
		[isExpanded, entries, visibleCount],
	);

	// ARIA label reflects the next action available to assistive technologies.
	const ariaExpandedLabel = useMemo(
		() => (isExpanded ? ARIA_LABELS.SHOW_FEWER_ENTRIES : ARIA_LABELS.SHOW_MORE_ENTRIES),
		[isExpanded],
	);

	// -------------------------------------------------------------
	// Actions
	// -------------------------------------------------------------
	// Expanding shows all entries.
	// Collapsing resets `visibleCount` back to the original collapsed limit.
	// The optional callback is invoked in both paths.
	const handleShowMore = () => {
		if (isExpanded) {
			setVisibleCount(initialVisibleCount);
			setIsExpanded(false);
		} else {
			setIsExpanded(true);
		}

		onShowMore?.();
	};

	return {
		ariaExpandedLabel,
		handleShowMore,
		hasMoreEntries,
		isExpanded,
		shouldShowMoreButton,
		visibleCount,
		visibleEntries,
	};
};

export default useScoreHistoryCard;
